import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { apiFetch } from '../utils/api';

const LeafletMap = lazy(() => import('./LeafletMap'));

interface LocationData {
  country: string;
  city: string | null;
  ll: [number, number] | null;
  count: number;
}

interface ActivityItem {
  userId: string;
  message: string;
  channel: string;
  timestamp: number;
}

interface RealTimeData {
  activeNow: number;
  todayMessages: number;
  todayUsers: number;
  allTimeMessages: number;
  recentActivity: ActivityItem[];
  locations: LocationData[];
  channels: { whatsapp: number; webchat: number; facebook: number; voice: number; email: number };
}

const COUNTRY_NAMES: Record<string, string> = {
  ZW: 'Zimbabwe', US: 'United States', GB: 'United Kingdom', ZA: 'South Africa',
  NG: 'Nigeria', KE: 'Kenya', GH: 'Ghana', TZ: 'Tanzania', UG: 'Uganda',
  ZM: 'Zambia', MW: 'Malawi', MZ: 'Mozambique', BW: 'Botswana', NA: 'Namibia',
  IN: 'India', CN: 'China', BR: 'Brazil', DE: 'Germany', FR: 'France',
  CA: 'Canada', AU: 'Australia', NL: 'Netherlands', SG: 'Singapore', AE: 'UAE',
};

const CHANNEL_ICONS: Record<string, string> = {
  whatsapp: '📱', webchat: '💬', facebook: '📘', voice: '🎙️', email: '📧',
};

const CHANNEL_COLORS: Record<string, string> = {
  whatsapp: '#25D366', webchat: '#5B4FE8', facebook: '#1877F2', voice: '#FF6B35', email: '#E94E77',
};

function timeAgo(ts: number): string {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

function getCountryName(code: string): string {
  return COUNTRY_NAMES[code] || code;
}

function StatCard({ label, value, sub, accent }: { label: string; value: string | number; sub?: string; accent?: string }) {
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '12px',
      padding: '16px 20px', flex: 1, minWidth: 120,
    }}>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: '28px', fontWeight: '700', color: accent || 'var(--text-primary)', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>{sub}</div>}
    </div>
  );
}

export default function RealTimePanel({ botId }: { botId: string }) {
  const [data, setData] = useState<RealTimeData | null>(null);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef<any>(null);
  const countdownRef = useRef<HTMLSpanElement>(null);

  const load = useCallback(async () => {
    try {
      const result = await apiFetch(`/api/realtime/${botId}`).then(r => r.json());
      setData(result);
    } catch {}
    setLoading(false);
    if (countdownRef.current) countdownRef.current.textContent = '30';
  }, [botId]);

  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, 30000);
    return () => clearInterval(intervalRef.current);
  }, [load]);

  useEffect(() => {
    let s = 30;
    const tick = setInterval(() => {
      s = Math.max(0, s - 1);
      if (countdownRef.current) countdownRef.current.textContent = String(s);
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  if (loading) return (
    <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading real-time data…</div>
  );

  const locWithCoords = (data?.locations || []).filter(l => l.ll);
  const maxCount = Math.max(...(data?.locations || []).map(l => l.count), 1);
  const totalChannelMsgs = Object.values(data?.channels || {}).reduce((a, b) => a + b, 0);

  return (
    <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)' }}>Real-Time Analytics</h2>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>Live view · refreshes in <span ref={countdownRef}>30</span>s</div>
        </div>
        <button onClick={load} style={{
          padding: '7px 16px', borderRadius: '8px', border: '1px solid var(--card-border)',
          background: 'var(--bg-element)', color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer',
        }}>↺ Refresh now</button>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '24px' }}>
        <StatCard label="Active Now" value={data?.activeNow ?? 0} sub="last 5 minutes" accent={data?.activeNow ? '#4ade80' : undefined} />
        <StatCard label="Today's Messages" value={(data?.todayMessages ?? 0).toLocaleString()} sub="since midnight" />
        <StatCard label="Today's Users" value={(data?.todayUsers ?? 0).toLocaleString()} sub="unique contacts" />
        <StatCard label="All-Time Messages" value={(data?.allTimeMessages ?? 0).toLocaleString()} />
      </div>

      {/* Map + Activity row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '16px', marginBottom: '16px' }}>

        {/* Map */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '12px', overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid var(--card-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>User Locations</span>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{data?.locations.length ?? 0} countries</span>
          </div>
          <div style={{ height: 340 }}>
            {locWithCoords.length > 0 ? (
              <Suspense fallback={<div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>Loading map…</div>}>
                <LeafletMap locWithCoords={locWithCoords} maxCount={maxCount} getCountryName={getCountryName} />
              </Suspense>
            ) : (
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', gap: 8 }}>
                <span style={{ fontSize: '32px' }}>🌍</span>
                <span style={{ fontSize: '13px' }}>No location data yet</span>
                <span style={{ fontSize: '11px', opacity: 0.7 }}>Location tracking works for Web Chat users</span>
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '12px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid var(--card-border)' }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>Recent Activity</span>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: '8px' }}>last hour</span>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', maxHeight: 320 }}>
            {(data?.recentActivity || []).length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>No activity in the last hour</div>
            ) : (data?.recentActivity || []).map((item, i) => (
              <div key={i} style={{
                padding: '10px 16px', borderBottom: '1px solid var(--card-border)',
                display: 'flex', gap: '10px', alignItems: 'flex-start',
              }}>
                <span style={{ fontSize: '16px', flexShrink: 0, marginTop: '1px' }}>{CHANNEL_ICONS[item.channel] || '💬'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.message || '(empty)'}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                    {timeAgo(item.timestamp)} · {item.channel}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom row: Countries + Channels */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>

        {/* Country list */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '12px', overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid var(--card-border)' }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>Top Countries</span>
          </div>
          <div style={{ padding: '8px 0' }}>
            {(data?.locations || []).length === 0 ? (
              <div style={{ padding: '20px 16px', color: 'var(--text-muted)', fontSize: '12px', textAlign: 'center' }}>
                No location data · applies to Web Chat only
              </div>
            ) : (data?.locations || []).slice(0, 8).map(loc => {
              const pct = Math.round((loc.count / maxCount) * 100);
              return (
                <div key={loc.country} style={{ padding: '7px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '13px', width: '20px', textAlign: 'center' }}>
                    {/* Flag emoji from country code */}
                    {loc.country.split('').map(c => String.fromCodePoint(c.charCodeAt(0) + 127397)).join('')}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--text-primary)' }}>{getCountryName(loc.country)}</span>
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{loc.count}</span>
                    </div>
                    <div style={{ height: '3px', background: 'var(--bg-element)', borderRadius: '2px' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: '#5B4FE8', borderRadius: '2px', transition: 'width 0.5s' }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Channel breakdown */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '12px', overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid var(--card-border)' }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>Today by Channel</span>
          </div>
          <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {Object.entries(data?.channels || {}).map(([ch, count]) => {
              const pct = totalChannelMsgs ? Math.round((count / totalChannelMsgs) * 100) : 0;
              return (
                <div key={ch}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{CHANNEL_ICONS[ch]} {ch.charAt(0).toUpperCase() + ch.slice(1)}</span>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{count.toLocaleString()} ({pct}%)</span>
                  </div>
                  <div style={{ height: '5px', background: 'var(--bg-element)', borderRadius: '3px' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: CHANNEL_COLORS[ch] || '#5B4FE8', borderRadius: '3px', transition: 'width 0.5s' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
}
